import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'

import {
  BoxContainer,
  Button,
  Loading,
  StatusBadge
} from '../../../../components'
import { Content } from '../../../../components/Content'
import { ecbrUserRoleListForSelect } from '../../../../constants/roles'
import { useInfinitePagination } from '../../../../hooks/useInfinitePagination'
import ecbrApi from '../../../../services/ecbrApi'
import { UserEvents } from '../constants'
import Pencil from '../../../../assets/Icons/Pencil'
import ErrorMessage from '../../../../components/Form/ErrorMessage'
import { extractErrorMessage } from '../../../../helpers/extractErrorMessage'
import { getDate } from '../../../../utils/dateUtils'
import { phoneMask } from '../../../../utils/phoneMask'
import { Formik } from 'formik'
import { isAvailableEmail } from '../../../../helpers/isAvailableEmail'
import { onlyNumbers } from '../../../../utils/onlyNumbers'
import Input from '../../../../components/Form/Input'
import Select from '../../../../components/Form/Select'
import { ComponentTitle } from '../../styles'
import { Form } from '../../../../components/Form/styles'
import BanIcon from '../../../../assets/Icons/BanIcon'
import CheckedIcon from '../../../../assets/Icons/CheckedIcon'
import { UserStatus, UserStatusColors } from '../../../../constants/user'
import { TooltipSpan } from '../../../../components/Tooltip'

// Example of a user:
// const user = {
//   id: 1,
//   name: 'Teste',
//   lastname: 'Um',
//   cellphone: '11996563535',
//   email: 'teste@teste.com',
//   googleId: null,
//   hashForgotPasswordExpiration: null,
//   hashForgotPassword: null,
//   role: 'ADMIN_USER',
//   statusSend: null,
//   active: true,
//   createdAt: '2022-03-17T20:38:04.700Z',
//   updatedAt: '2022-03-17T20:38:04.700Z'
// }

const pagination = { page: 1, take: 5 }

const UserList = ({ eventListener }) => {
  const [error, setError] = useState('')
  const [searchParameters, _setSearchParameters] = useState({
    ...pagination
  })
  const [userEditing, setUserEditing] = useState(null)

  const {
    data: users,
    fetchNextPage,
    hasNextPage,
    isLastPage,
    isFetching,
    isLoading,
    refetch
  } = useInfinitePagination(
    'userList',
    async (params) => {
      const { pageParam = searchParameters.page } = params
      return ecbrApi.users.getCompanyUsers({
        ...searchParameters,
        page: pageParam || searchParameters.page
      })
    },
    {
      cacheTime: 0,
      onSuccess: () => {
        setError('')
      },
      onError: () => {
        setError('Erro ao carregar os usuários')
      }
    }
  )

  useEffect(() => {
    eventListener.subscribe(UserEvents.USER_UPDATED, refetch)
    eventListener.subscribe(UserEvents.USER_DELETED, refetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveUser = async ({ role, ...values }) => {
    try {
      setError('')
      const payload = {
        ...values,
        role: role.value ?? role
      }
      await ecbrApi.users.updateCompanyUser(userEditing.id, payload)
      setUserEditing(null)
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      setError(`Erro ao salvar o usuário: ${errorMessage}`)
    }

    eventListener?.publish(UserEvents.USER_UPDATED)
  }

  const handleDeactivate = async (user) => {
    try {
      setError('')
      await ecbrApi.users.deactivateCompanyUser(user.id)
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      setError(`Erro ao desativar o usuário: ${errorMessage}`)
    }

    eventListener?.publish(UserEvents.USER_DELETED)
  }

  const handleActivate = async (user) => {
    try {
      setError('')
      await ecbrApi.users.activateCompanyUser(user.id)
    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      setError(`Erro ao ativar o usuário: ${errorMessage}`)
    }

    eventListener?.publish(UserEvents.USER_UPDATED)
  }

  return (
    <BoxContainer>
      <ComponentTitle>
        Gerenciar usuários
        {(isLoading || isFetching) && <Loading />}
      </ComponentTitle>

      <Content templateColumns="10% 10% 13% 9% 11% 7% 9% 6% 9%">
        <header>
          <ul>
            <li>Nome</li>
            <li>Sobrenome</li>
            <li>E-mail</li>
            <li>Celular</li>
            <li>Perfil</li>
            <li>Status</li>
            <li>Data de atualização</li>
            <li>Ações</li>
            <li>&nbsp;</li>
          </ul>
        </header>
        <main>
          {users?.map((user) => (
            <React.Fragment key={user.id}>
              {userEditing?.id === user.id ? (
                <Formik
                  initialValues={{
                    name: user.name,
                    lastname: user.lastname,
                    email: user.email,
                    cellphone: user.cellphone && phoneMask(user.cellphone),
                    role: ecbrUserRoleListForSelect.find(
                      (role) => role.value === user.role
                    )
                  }}
                  validationSchema={Yup.object().shape({
                    name: Yup.string().required('Nome obrigatório'),
                    lastname: Yup.string().required('Sobrenome obrigatório'),
                    email: Yup.string()
                      .required('Email obrigatório')
                      .email('Email inválido')
                      .test(
                        'email',
                        'Email já cadastrado',
                        async (value) =>
                          value === user.email ||
                          ((await isAvailableEmail(value)) ?? true)
                      ),
                    cellphone: Yup.string()
                      .required('Celular obrigatório')
                      .test(
                        'cellphone',
                        'Celular inválido',
                        (value) => onlyNumbers(value).length === 11
                      )
                      .matches(
                        /^\([1-9]{2}\) (?:[2-8]|9[1-9])[0-9]{3}\-[0-9]{4}$/,
                        'Celular inválido'
                      ),
                    role: Yup.string()
                      .transform((_, obj) => (obj ? obj.value : undefined))
                      .required('Perfil obrigatório')
                  })}
                  onSubmit={async (values) => {
                    const { name, lastname, email, cellphone, role } = values
                    const newUser = {
                      ...user,
                      name,
                      lastname,
                      email,
                      cellphone: cellphone.replace(/\D/g, ''),
                      role: role.value
                    }
                    await saveUser(newUser)
                  }}
                >
                  {({ errors }) => (
                    <>
                      <Form display="block" maxWidth="100%" margin="0">
                        <ul>
                          <li>
                            <Input
                              name="name"
                              placeholder="Nome"
                              showValidIcon
                              hideErrorMessage
                              display="inline-block"
                            />
                          </li>
                          <li>
                            <Input
                              name="lastname"
                              placeholder="Sobrenome"
                              showValidIcon
                              hideErrorMessage
                              display="inline-block"
                            />
                          </li>
                          <li>{user.email}</li>
                          <li>{user.cellphone && phoneMask(user.cellphone)}</li>
                          <li>
                            <Select
                              name="role"
                              options={ecbrUserRoleListForSelect}
                              showValidIcon
                              hideErrorMessage
                              display="inline-block"
                            />
                          </li>
                          <li>
                            <StatusBadge
                              text={UserStatus[user.active]}
                              color={UserStatusColors[user.active]}
                            />
                          </li>
                          <li>{getDate(user.updatedAt)}</li>
                          <li>
                            {user.active ? (
                              <TooltipSpan content="Desativar usuário">
                                <div onClick={() => handleDeactivate(user)}>
                                  <BanIcon color="#858585" />
                                </div>
                              </TooltipSpan>
                            ) : (
                              <TooltipSpan content="Ativar usuário">
                                <div onClick={() => handleActivate(user)}>
                                  <CheckedIcon color="#858585" />
                                </div>
                              </TooltipSpan>
                            )}
                          </li>
                          <li>
                            <Button
                              type="submit"
                              variant="outlinedLink"
                              text="Salvar"
                              padding="0"
                            />
                            <Button
                              type="button"
                              variant="outlinedLink"
                              text="Cancelar"
                              padding="0"
                              onClick={() => setUserEditing(null)}
                            />
                          </li>
                        </ul>
                      </Form>
                      {Object.keys(errors).length > 0 && (
                        <ErrorMessage>{Object.values(errors)[0]}</ErrorMessage>
                      )}
                    </>
                  )}
                </Formik>
              ) : (
                <ul>
                  <li>{user.name}</li>
                  <li>{user.lastname}</li>
                  <li>{user.email}</li>
                  <li>{user.cellphone && phoneMask(user.cellphone)}</li>
                  <li>
                    {ecbrUserRoleListForSelect.find(
                      ({ value }) => value === user.role
                    )?.label || user.role}
                  </li>
                  <li>
                    <StatusBadge
                      text={UserStatus[user.active]}
                      color={UserStatusColors[user.active]}
                    />
                  </li>
                  <li>{getDate(user.updatedAt)}</li>
                  <li>
                    {(!userEditing || userEditing.id !== user.id) && (
                      <TooltipSpan content="Editar usuário">
                        <div onClick={() => setUserEditing(user)}>
                          <Pencil color="#858585" />
                        </div>
                      </TooltipSpan>
                    )}
                    {user.active ? (
                      <TooltipSpan content="Desativar usuário">
                        <div onClick={() => handleDeactivate(user)}>
                          <BanIcon color="#858585" />
                        </div>
                      </TooltipSpan>
                    ) : (
                      <TooltipSpan content="Ativar usuário">
                        <div onClick={() => handleActivate(user)}>
                          <CheckedIcon color="#858585" />
                        </div>
                      </TooltipSpan>
                    )}
                  </li>
                  <li></li>
                </ul>
              )}
            </React.Fragment>
          ))}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {hasNextPage && !isLastPage && (
            <Button
              type="button"
              variant="outlined"
              text={isLoading || isFetching ? 'Carregando...' : 'Ver mais'}
              width="120px"
              onClick={() => fetchNextPage()}
            />
          )}
        </main>
      </Content>
    </BoxContainer>
  )
}

export default UserList
